import React, { useEffect, useMemo, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import * as XLSX from 'xlsx';
import { ChevronLeft, ChevronRight, FileText, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { UnifiedTable, UnifiedTableColumn } from '@/components/ui/unifiedTable/UnifiedTable';
import { toast } from '@/components/ui/toast';
import { OdrLoadingSpinner } from '@/components/ui/spinner';
import { useUser } from '@/store/useUser';
import { useToolEditingPresence } from '../../candidates/useToolEditingPresence';
import { hasAnyPolicy } from '@/utils/policies';

const LIST_HOUSING_DASHBOARD = gql`
  query HousingDashboardList {
    housingDashboardList {
      totalCount
      items {
        id
        social_program
        social_program_label
        site
        address
        lat
        lon
        bed
        rate
        startdate
        closing_date
        service_planning_area
        supervisorial_district
        odr_nurse
        odr_housing_staff
        odr_social_worker
        psychiatrist
        contact
        nurse_contact
        clinical_contact
        notes
        work_order
        active
      }
    }
    housingDashboardMeta {
      programOptions {
        value
        label
      }
      exportProgramOptions {
        value
        label
      }
      searchFieldOptions {
        value
        label
      }
    }
  }
`;

const CREATE_HOUSING_DASHBOARD_ITEM = gql`
  mutation CreateHousingDashboardItem($input: HousingDashboardInput!) {
    createHousingDashboardItem(input: $input) {
      id
    }
  }
`;

const UPDATE_HOUSING_DASHBOARD_ITEM = gql`
  mutation UpdateHousingDashboardItem($id: Int!, $input: HousingDashboardInput!) {
    updateHousingDashboardItem(id: $id, input: $input) {
      id
    }
  }
`;

const UPDATE_HOUSING_DASHBOARD_CELL = gql`
  mutation UpdateHousingDashboardCell($id: Int!, $field: String!, $value: String) {
    updateHousingDashboardCell(id: $id, field: $field, value: $value) {
      id
    }
  }
`;

const DELETE_HOUSING_DASHBOARD_ITEM = gql`
  mutation DeleteHousingDashboardItem($id: Int!) {
    deleteHousingDashboardItem(id: $id) {
      message
      responseCode
      responseID
    }
  }
`;

const TOGGLE_HOUSING_DASHBOARD_ITEM_ACTIVE = gql`
  mutation ToggleHousingDashboardItemActive($id: Int!) {
    toggleHousingDashboardItemActive(id: $id) {
      id
      active
    }
  }
`;

const PAGE_SIZE = 100;
const NONE_SELECT_VALUE = '__none__';

const SEARCHABLE_FIELDS = new Set(['bed', 'contact', 'address']);

const ROW_COLORS: Record<string, string> = {
  MIST: 'var(--neutral-gray-10)',
  FIST: 'var(--neutral-gray-20)',
  'DSH Diversion': 'var(--neutral-gray-10)',
  'Maternal Health': 'var(--neutral-gray-20)',
  'Maternal Health ICMS': 'var(--neutral-gray-10)',
  'LEAD ICMS': 'var(--neutral-gray-20)',
  'ODR Housing ICMS': 'var(--neutral-gray-10)',
  'ODR Housing Interim': 'var(--neutral-gray-20)',
  'ODR Housing B&C': 'var(--neutral-gray-10)',
  'ODR Housing Project-Based': 'var(--neutral-gray-20)',
  'ODR 200': 'var(--neutral-gray-10)',
  ERC: 'var(--neutral-gray-20)',
  Acute: 'var(--neutral-gray-10)',
  Subacute: 'var(--neutral-gray-20)',
  RCC: 'var(--neutral-gray-10)',
  Reentry: 'var(--neutral-gray-20)',
};

const EXPORT_COLUMNS: Array<{ key: keyof HousingDashboardItem; label: string }> = [
  { key: 'id', label: '' },
  { key: 'social_program_label', label: 'Program' },
  { key: 'site', label: 'Site' },
  { key: 'address', label: 'Address' },
  { key: 'bed', label: 'Bed' },
  { key: 'rate', label: 'Rate' },
  { key: 'startdate', label: 'Start Date' },
  { key: 'closing_date', label: 'Closing Date' },
  { key: 'service_planning_area', label: 'Service Planning Area (SPA)' },
  { key: 'supervisorial_district', label: 'Supervisorial District (SD)' },
  { key: 'odr_nurse', label: 'ODR Nurse' },
  { key: 'psychiatrist', label: 'Psychiatrist' },
  { key: 'odr_housing_staff', label: 'ODR Housing Staff' },
  { key: 'odr_social_worker', label: 'ODR Social Worker' },
  { key: 'contact', label: 'Prgm Mgmt Contact' },
  { key: 'nurse_contact', label: 'Prgm Nurse Contact' },
  { key: 'clinical_contact', label: 'Prgm Clinical/CM Contact' },
  { key: 'notes', label: 'Notes' },
  { key: 'work_order', label: 'Work-Order' },
  { key: 'active', label: 'Active' },
];

const INLINE_FIELDS = ['social_program', 'site', 'address', 'bed', 'rate', 'contact', 'startdate', 'closing_date', 'service_planning_area', 'supervisorial_district', 'odr_nurse', 'odr_housing_staff', 'odr_social_worker', 'psychiatrist', 'nurse_contact', 'clinical_contact', 'notes', 'work_order'] as const;
type InlineHousingField = (typeof INLINE_FIELDS)[number];
const INLINE_TEXT_FIELDS = INLINE_FIELDS.filter(
  (field): field is Exclude<InlineHousingField, 'social_program'> => field !== 'social_program',
);

type HousingDashboardItem = {
  id: number;
  social_program?: string | null;
  social_program_label?: string | null;
  site?: string | null;
  address?: string | null;
  lat?: string | null;
  lon?: string | null;
  bed?: string | null;
  rate?: string | null;
  startdate?: string | null;
  closing_date?: string | null;
  service_planning_area?: string | null;
  supervisorial_district?: string | null;
  odr_nurse?: string | null;
  odr_housing_staff?: string | null;
  odr_social_worker?: string | null;
  psychiatrist?: string | null;
  contact?: string | null;
  nurse_contact?: string | null;
  clinical_contact?: string | null;
  notes?: string | null;
  work_order?: string | null;
  active?: boolean | null;
};

type HousingDashboardOption = {
  value: string;
  label: string;
};

type HousingDashboardData = {
  housingDashboardList: {
    totalCount: number;
    items: HousingDashboardItem[];
  };
  housingDashboardMeta: {
    programOptions: HousingDashboardOption[];
    exportProgramOptions: HousingDashboardOption[];
    searchFieldOptions: HousingDashboardOption[];
  };
};

const EMPTY_HOUSING_ITEMS: HousingDashboardItem[] = [];
const EMPTY_HOUSING_OPTIONS: HousingDashboardOption[] = [];

type HousingDashboardFormState = {
  id: number | null;
  social_program: string;
  site: string;
  address: string;
  lat: string;
  lon: string;
  bed: string;
  rate: string;
  startdate: string;
  closing_date: string;
  service_planning_area: string;
  supervisorial_district: string;
  odr_nurse: string;
  odr_housing_staff: string;
  odr_social_worker: string;
  psychiatrist: string;
  contact: string;
  nurse_contact: string;
  clinical_contact: string;
  notes: string;
  work_order: string;
  active: boolean;
};

const emptyFormState: HousingDashboardFormState = {
  id: null,
  social_program: '',
  site: '',
  address: '',
  lat: '',
  lon: '',
  bed: '',
  rate: '',
  startdate: '',
  closing_date: '',
  service_planning_area: '',
  supervisorial_district: '',
  odr_nurse: '',
  odr_housing_staff: '',
  odr_social_worker: '',
  psychiatrist: '',
  contact: '',
  nurse_contact: '',
  clinical_contact: '',
  notes: '',
  work_order: '',
  active: true,
};

const formSections: Array<Array<{ key: keyof HousingDashboardFormState; label: string; type?: 'textarea' | 'date' }>> = [
  [
    { key: 'social_program', label: 'Program' },
    { key: 'active', label: 'Active' },
    { key: 'site', label: 'Site' },
    { key: 'address', label: 'Address' },
    { key: 'bed', label: 'Bed' },
    { key: 'rate', label: 'Rate' },
    { key: 'lat', label: 'Latitude' },
    { key: 'lon', label: 'Longitude' },
    { key: 'startdate', label: 'Start Date', type: 'date' },
    { key: 'closing_date', label: 'Closing Date', type: 'date' },
    { key: 'service_planning_area', label: 'Service Planning Area (SPA)' },
    { key: 'supervisorial_district', label: 'Supervisorial District (SD)' },
  ],
  [
    { key: 'odr_nurse', label: 'ODR Nurse' },
    { key: 'odr_housing_staff', label: 'ODR Housing Staff' },
    { key: 'odr_social_worker', label: 'ODR Social Worker' },
    { key: 'psychiatrist', label: 'Psychiatrist' },
    { key: 'contact', label: 'Prgm Mgmt Contact' },
    { key: 'nurse_contact', label: 'Prgm Nurse Contact' },
    { key: 'clinical_contact', label: 'Prgm Clinical/CM Contact' },
    { key: 'notes', label: 'Notes', type: 'textarea' },
    { key: 'work_order', label: 'Work-Order', type: 'textarea' },
  ],
];

const normalizeText = (value: string) => value.trim();

const normalizeRate = (value: string) => {
  const raw = value.trim();
  if (!raw) {
    return '';
  }

  const parsed = Number.parseFloat(raw);
  if (Number.isNaN(parsed) || parsed === 0) {
    return '';
  }

  return `${parsed}`;
};

const normalizeFormState = (item?: HousingDashboardItem | null): HousingDashboardFormState => {
  if (!item) {
    return emptyFormState;
  }

  return {
    id: item.id,
    social_program: item.social_program || '',
    site: item.site || '',
    address: item.address || '',
    lat: item.lat || '',
    lon: item.lon || '',
    bed: item.bed || '',
    rate: item.rate || '',
    startdate: item.startdate || '',
    closing_date: item.closing_date || '',
    service_planning_area: item.service_planning_area || '',
    supervisorial_district: item.supervisorial_district || '',
    odr_nurse: item.odr_nurse || '',
    odr_housing_staff: item.odr_housing_staff || '',
    odr_social_worker: item.odr_social_worker || '',
    psychiatrist: item.psychiatrist || '',
    contact: item.contact || '',
    nurse_contact: item.nurse_contact || '',
    clinical_contact: item.clinical_contact || '',
    notes: item.notes || '',
    work_order: item.work_order || '',
    active: Boolean(item.active),
  };
};

const compareValues = (left: unknown, right: unknown, direction: 'asc' | 'desc') => {
  const leftValue = `${left ?? ''}`.trim().toLowerCase();
  const rightValue = `${right ?? ''}`.trim().toLowerCase();

  if (leftValue === rightValue) {
    return 0;
  }

  if (direction === 'asc') {
    return leftValue > rightValue ? 1 : -1;
  }

  return leftValue < rightValue ? 1 : -1;
};

const getRowStyle = (item: HousingDashboardItem): React.CSSProperties => {
  if (!item.active) {
    return {
      backgroundColor: 'var(--neutral-gray-30)',
    };
  }

  const label = `${item.social_program_label || item.social_program || ''}`.trim();
  const backgroundColor = ROW_COLORS[label];
  if (!backgroundColor) {
    return {};
  }

  return { backgroundColor };
};

const usesLightRowText = (_item: HousingDashboardItem) => false;

export const HousingDashboard = () => {
  const { user, isUserLoading } = useUser();
  const canAccess = hasAnyPolicy(user, ['tools:housing~full', 'admin:access~full']);

  const [searchText, setSearchText] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [searchField, setSearchField] = useState('all');
  const [programFilter, setProgramFilter] = useState('all');
  const [activeFilter, setActiveFilter] = useState('all');
  const [sortField, setSortField] = useState<keyof HousingDashboardItem>('id');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportError, setExportError] = useState('');
  const [formState, setFormState] = useState<HousingDashboardFormState>(emptyFormState);
  const [exportProgram, setExportProgram] = useState('all');
  const [exportActive, setExportActive] = useState('all');
  const [cellDrafts, setCellDrafts] = useState<Record<string, string>>({});
  const [savingCellKeys, setSavingCellKeys] = useState<string[]>([]);
  const pendingCellWrites = React.useRef(new Set<string>());
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [togglingItemId, setTogglingItemId] = useState<number | null>(null);
  const presence = useToolEditingPresence('housing', canAccess && Boolean(user?.username), page, dialogOpen ? formState.id || null : null);

  const { data, loading, error, refetch } = useQuery<HousingDashboardData>(LIST_HOUSING_DASHBOARD, {
    fetchPolicy: 'cache-and-network',
    skip: !canAccess,
    pollInterval: dialogOpen ? 0 : 30000,
  });

  const [createItem] = useMutation<{ createHousingDashboardItem: { id: number } | null }>(CREATE_HOUSING_DASHBOARD_ITEM);
  const [updateItem] = useMutation<{ updateHousingDashboardItem: { id: number } | null }>(UPDATE_HOUSING_DASHBOARD_ITEM);
  const [updateItemCell] = useMutation<{ updateHousingDashboardCell: { id: number } | null }>(UPDATE_HOUSING_DASHBOARD_CELL);
  const [deleteItem] = useMutation<{ deleteHousingDashboardItem: { responseCode: number | null; responseID: number | null } | null }>(DELETE_HOUSING_DASHBOARD_ITEM);
  const [toggleItemActive] = useMutation<{ toggleHousingDashboardItemActive: { id: number; active: boolean | null } | null }>(TOGGLE_HOUSING_DASHBOARD_ITEM_ACTIVE);

  const programOptions = data?.housingDashboardMeta?.programOptions ?? EMPTY_HOUSING_OPTIONS;
  const exportProgramOptions = data?.housingDashboardMeta?.exportProgramOptions ?? EMPTY_HOUSING_OPTIONS;
  const searchOptions = data?.housingDashboardMeta?.searchFieldOptions ?? EMPTY_HOUSING_OPTIONS;
  const items = data?.housingDashboardList?.items ?? EMPTY_HOUSING_ITEMS;

  const filteredItems = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();

    return items
      .filter((item) => {
        if (programFilter !== 'all' && item.social_program !== programFilter) {
          return false;
        }

        if (activeFilter === 'yes' && !item.active) {
          return false;
        }

        if (activeFilter === 'no' && item.active) {
          return false;
        }

        if (!normalizedSearch) {
          return true;
        }

        if (searchField !== 'all' && SEARCHABLE_FIELDS.has(searchField)) {
          return `${item[searchField as keyof HousingDashboardItem] || ''}`.toLowerCase().includes(normalizedSearch);
        }

        const bucket = EXPORT_COLUMNS.filter(({ key }) => key !== 'id').map(({ key }) => item[key])
          .map((value) => `${value || ''}`.toLowerCase())
          .join(' ');

        return bucket.includes(normalizedSearch);
      })
      .sort((left, right) => {
        if (sortField === 'id') {
          const leftValue = Number(left.id || 0);
          const rightValue = Number(right.id || 0);
          if (leftValue === rightValue) {
            return 0;
          }

          if (sortDirection === 'asc') {
            return leftValue - rightValue;
          }

          return rightValue - leftValue;
        }

        return compareValues(left[sortField], right[sortField], sortDirection);
      });
  }, [activeFilter, items, programFilter, searchField, searchText, sortDirection, sortField]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE));
  const displayedPage = Math.min(page, totalPages);
  const hasActiveFilters =
    searchText.trim().length > 0
    || searchField !== 'all'
    || programFilter !== 'all'
    || activeFilter !== 'all';
  const hasSecondaryFilters =
    searchField !== 'all'
    || programFilter !== 'all'
    || activeFilter !== 'all';

  useEffect(() => {
    if (page <= totalPages) {
      return;
    }

    setPage(totalPages);
  }, [page, totalPages]);

  const pagedItems = useMemo(() => {
    const safePage = Math.min(page, totalPages);
    const start = (safePage - 1) * PAGE_SIZE;
    return filteredItems.slice(start, start + PAGE_SIZE);
  }, [filteredItems, page, totalPages]);

  const setField = (key: keyof HousingDashboardFormState, value: string | boolean) => {
    setFormState((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const openCreateDialog = () => {
    setFormState(emptyFormState);
    setDialogOpen(true);
  };

  const openEditDialog = (item: HousingDashboardItem) => {
    setFormState(normalizeFormState(item));
    setDialogOpen(true);
  };

  const openExportDialog = () => {
    setExportProgram('all');
    setExportActive('all');
    setExportError('');
    setExportDialogOpen(true);
  };

  const getCellKey = (id: number, field: InlineHousingField) => `${id}:${field}`;

  const getCellValue = (item: HousingDashboardItem, field: InlineHousingField) => {
    const key = getCellKey(item.id, field);
    if (Object.prototype.hasOwnProperty.call(cellDrafts, key)) {
      return cellDrafts[key];
    }

    if (field === 'social_program') {
      return item.social_program || NONE_SELECT_VALUE;
    }

    return `${item[field] || ''}`;
  };

  const setCellValue = (item: HousingDashboardItem, field: InlineHousingField, value: string) => {
    const key = getCellKey(item.id, field);
    setCellDrafts((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const clearCellDraft = (id: number, field: InlineHousingField) => {
    const key = getCellKey(id, field);
    setCellDrafts((current) => {
      if (!Object.prototype.hasOwnProperty.call(current, key)) {
        return current;
      }

      const next = { ...current };
      delete next[key];
      return next;
    });
  };

  const normalizeInlineDraft = (field: InlineHousingField, value: string) => {
    if (field === 'social_program') {
      if (value === NONE_SELECT_VALUE) {
        return '';
      }

      return normalizeText(value);
    }

    if (field === 'rate') {
      return normalizeRate(value);
    }

    return normalizeText(value);
  };

  const refreshAfterWrite = async () => {
    try {
      await refetch();
    } catch {
      toast.error('The housing change succeeded, but the list could not be refreshed. Refresh the page.');
    }
  };

  const commitCellValue = async (item: HousingDashboardItem, field: InlineHousingField, overrideValue?: string) => {
    const key = getCellKey(item.id, field);
    if (pendingCellWrites.current.has(key)) return;
    const hasDraft = Object.prototype.hasOwnProperty.call(cellDrafts, key);
    const draftValue = overrideValue ?? (hasDraft ? cellDrafts[key] : getCellValue(item, field));
    const normalizedDraft = normalizeInlineDraft(field, draftValue);
    const normalizedCurrent = normalizeInlineDraft(field, `${item[field] || ''}`);

    if (normalizedDraft === normalizedCurrent) {
      clearCellDraft(item.id, field);
      return;
    }

    pendingCellWrites.current.add(key);
    setSavingCellKeys((current) => [...current, key]);

    try {
      const response = await updateItemCell({
        variables: {
          id: item.id,
          field,
          value: normalizedDraft,
        },
      });
      if (response.errors?.length || response.data?.updateHousingDashboardCell?.id !== item.id) throw new Error('Housing site update was not confirmed');
      toast.success('Housing site updated');
      await refreshAfterWrite();
      clearCellDraft(item.id, field);
    } catch {
      toast.error('Failed to update housing site');
    } finally {
      pendingCellWrites.current.delete(key);
      setSavingCellKeys((current) => current.filter((savedKey) => savedKey !== key));
    }
  };

  const closeDialog = () => {
    if (isSaving) {
      return;
    }

    setDialogOpen(false);
  };

  const buildMutationInput = () => ({
    social_program: normalizeText(formState.social_program),
    site: normalizeText(formState.site),
    address: normalizeText(formState.address),
    lat: normalizeText(formState.lat),
    lon: normalizeText(formState.lon),
    bed: normalizeText(formState.bed),
    rate: normalizeText(formState.rate),
    startdate: normalizeText(formState.startdate),
    closing_date: normalizeText(formState.closing_date),
    service_planning_area: normalizeText(formState.service_planning_area),
    supervisorial_district: normalizeText(formState.supervisorial_district),
    odr_nurse: normalizeText(formState.odr_nurse),
    odr_housing_staff: normalizeText(formState.odr_housing_staff),
    odr_social_worker: normalizeText(formState.odr_social_worker),
    psychiatrist: normalizeText(formState.psychiatrist),
    contact: normalizeText(formState.contact),
    nurse_contact: normalizeText(formState.nurse_contact),
    clinical_contact: normalizeText(formState.clinical_contact),
    notes: normalizeText(formState.notes),
    work_order: normalizeText(formState.work_order),
    active: formState.active,
  });

  const handleSave = async () => {
    setIsSaving(true);

    try {
      const input = buildMutationInput();

      if (formState.id == null) {
        const response = await createItem({ variables: { input } });
        const id = response.data?.createHousingDashboardItem?.id;
        if (response.errors?.length || typeof id !== 'number' || !Number.isInteger(id) || id <= 0) throw new Error('Housing site creation was not confirmed');
        toast.success('Housing site created');
      }

      if (formState.id != null) {
        const response = await updateItem({
          variables: {
            id: formState.id,
            input,
          },
        });
        if (response.errors?.length || response.data?.updateHousingDashboardItem?.id !== formState.id) throw new Error('Housing site update was not confirmed');
        toast.success('Housing site updated');
      }

      setDialogOpen(false);
      await refreshAfterWrite();
    } catch {
      toast.error('Failed to save housing site');
    }

    setIsSaving(false);
  };

  const handleDelete = async (item: HousingDashboardItem) => {
    if (!window.confirm(`Delete housing site "${item.site || item.id}"?`)) {
      return;
    }

    setIsDeleting(true);

    try {
      const response = await deleteItem({ variables: { id: item.id } });
      const deletion = response.data?.deleteHousingDashboardItem;
      if (response.errors?.length || deletion?.responseCode !== 200 || deletion.responseID !== item.id) throw new Error('Housing site deletion was not confirmed');
      toast.success('Housing site deleted');
      await refreshAfterWrite();
    } catch {
      toast.error('Failed to delete housing site');
    }

    setIsDeleting(false);
  };

  const handleToggleActive = async (item: HousingDashboardItem) => {
    if (togglingItemId !== null) return;
    const actionLabel = item.active ? 'disable' : 'enable';
    if (!window.confirm(`Do you want to ${actionLabel} this housing site?`)) {
      return;
    }

    setTogglingItemId(item.id);
    try {
      const response = await toggleItemActive({ variables: { id: item.id } });
      const updated = response.data?.toggleHousingDashboardItemActive;
      if (response.errors?.length || updated?.id !== item.id || updated.active !== !item.active) throw new Error('Housing site status change was not confirmed');
      toast.success('Housing site status updated');
      await refreshAfterWrite();
    } catch {
      toast.error('Failed to update housing site status');
    } finally {
      setTogglingItemId(null);
    }
  };

  const handleRefresh = async () => {
    try {
      await refetch();
      toast.success('Housing dashboard refreshed');
    } catch {
      toast.error('Failed to refresh housing dashboard');
    }
  };

  const handleSearch = (value?: string) => {
    const nextSearch = typeof value === 'string' ? value : searchDraft;
    setSearchText(nextSearch.trim());
    setSearchDraft(nextSearch);
    setPage(1);
  };

  const handleResetFilters = () => {
    setSearchText('');
    setSearchDraft('');
    setSearchField('all');
    setProgramFilter('all');
    setActiveFilter('all');
    setPage(1);
  };

  const handleExport = () => {
    const rows = items
      .filter((item) => {
        if (exportProgram !== 'all' && item.social_program !== exportProgram) {
          return false;
        }

        if (exportActive === '1' && !item.active) {
          return false;
        }

        if (exportActive === '0' && item.active) {
          return false;
        }

        return true;
      })
      .map((item, index) => {
      const exportRow: Record<string, string | number> = {};
      EXPORT_COLUMNS.forEach(({ key, label }) => {
        if (key === 'id') {
          exportRow[label] = index + 1;
          return;
        }
        if (key === 'social_program_label') {
          exportRow[label] = `${item.social_program ?? ''}`;
          return;
        }

        if (key === 'rate') {
          exportRow[label] = item.rate && item.rate !== '0' ? `$${item.rate}` : item.rate ?? '';
          return;
        }

        if (key === 'active') {
          exportRow[label] = item.active ? '1' : '0';
          return;
        }

        exportRow[label] = `${item[key] ?? ''}`;
      });
      return exportRow;
    });

    const worksheet = XLSX.utils.json_to_sheet(rows, { header: EXPORT_COLUMNS.map(({ label }) => label) });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Housing Dashboard');
    const now = new Date();
    const month = `${now.getMonth() + 1}`.padStart(2, '0');
    const day = `${now.getDate()}`.padStart(2, '0');
    const year = `${now.getFullYear()}`;
    XLSX.writeFile(workbook, `housing_list_report${month}-${day}-${year}.xlsx`);
    setExportDialogOpen(false);
  };

  const downloadHousingReport = () => {
    setExportError('');
    try {
      handleExport();
    } catch {
      setExportError('The housing report could not be downloaded. Please try again.');
    }
  };

  const columns: UnifiedTableColumn<HousingDashboardItem>[] = [
    {
      key: 'id',
      header: 'ID',
      sortable: true,
      dataType: 'int',
      sortValue: (item) => item.id,
      accessor: (item) => (
        <span className={usesLightRowText(item) ? 'text-white' : undefined}>
          {item.id}
        </span>
      ),
    },
    {
      key: 'social_program_label',
      header: 'Program',
      sortable: true,
      sortValue: (item) => item.social_program_label || item.social_program || '',
      accessor: (item) => (
        <Select
          value={getCellValue(item, 'social_program')}
          disabled={savingCellKeys.includes(getCellKey(item.id, 'social_program'))}
          onValueChange={(value) => {
            setCellValue(item, 'social_program', value);
            void commitCellValue(item, 'social_program', value);
          }}
        >
          <SelectTrigger aria-label={`Program for housing item ${item.id}`} className="h-[30px] min-w-[11rem] text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE_SELECT_VALUE}>None</SelectItem>
            {programOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ),
    },
    ...INLINE_TEXT_FIELDS.map((field) => {
      const headerByField: Record<Exclude<InlineHousingField, 'social_program'>, string> = {
        site: 'Site',
        address: 'Address',
        bed: 'Bed',
        rate: 'Rate',
        contact: 'Primary Contact',
        startdate: 'Start Date', closing_date: 'Closing Date', service_planning_area: 'Service Planning Area (SPA)', supervisorial_district: 'Supervisorial District (SD)',
        odr_nurse: 'ODR Nurse', odr_housing_staff: 'ODR Housing Staff', odr_social_worker: 'ODR Social Worker', psychiatrist: 'Psychiatrist',
        nurse_contact: 'Prgm Nurse Contact', clinical_contact: 'Prgm Clinical/CM Contact', notes: 'Notes', work_order: 'Work-Order',
      };

      return {
        key: field,
        header: headerByField[field],
        sortable: true,
        sortValue: (item: HousingDashboardItem) => item[field] || '',
        accessor: (item: HousingDashboardItem) => {
          const cellKey = getCellKey(item.id, field);
          const isWideField = field === 'address' || field === 'contact';
          return (
            <Input
              id={`housing-${item.id}-${field}`}
              type={field === 'startdate' || field === 'closing_date' ? 'date' : 'text'}
              aria-label={`${headerByField[field]} for housing item ${item.id}`}
              value={getCellValue(item, field)}
              disabled={savingCellKeys.includes(cellKey)}
              className={isWideField ? 'h-8 min-w-[14rem] text-xs' : 'h-8 min-w-[7rem] text-xs'}
              onChange={(event) => setCellValue(item, field, event.target.value)}
              onBlur={() => void commitCellValue(item, field)}
              onKeyDown={(event) => {
                if (event.key !== 'Enter') {
                  return;
                }

                event.currentTarget.blur();
              }}
            />
          );
        },
      };
    }),
    {
      key: 'active',
      header: 'Active',
      sortable: true,
      sortValue: (item) => (item.active ? 1 : 0),
      accessor: (item) => (
        <span className={usesLightRowText(item) ? 'text-white' : undefined}>
          {item.active ? 'Yes' : 'No'}
        </span>
      ),
    },
  ];

  if (isUserLoading) {
    return <OdrLoadingSpinner message="Loading Housing Dashboard" />;
  }

  if (!canAccess) {
    return (
      <div className="p-4 md:p-6">
        <h1 className="text-2xl font-semibold">Housing Dashboard</h1>
        <p className="mt-2 text-sm text-[var(--neutral-gray-80)]">You do not have Housing Dashboard access.</p>
      </div>
    );
  }

  if (loading && !data) {
    return <OdrLoadingSpinner message="Loading Housing Dashboard" />;
  }

  if (error) {
    return (
      <div className="p-4 md:p-6">
        <h1 className="text-2xl font-semibold">Housing Dashboard</h1>
        <p role="alert" className="mt-2 text-sm text-[var(--semantic-error)]">Failed to load Housing Dashboard. Refresh the page and try again.</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      {presence.error && <p role="alert" className="mb-3 text-sm text-[var(--semantic-error)]">Editing presence could not be updated.</p>}
      {presence.editors.length > 0 && <p className="mb-3 text-sm text-[var(--neutral-gray-100)]">Editing now: {presence.editors.map((editor) => `${editor.username} (site ${editor.entityId})`).join(', ')}</p>}
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Housing Dashboard</h1>
          <p className="text-sm text-[var(--neutral-gray-80)]">
            Housing site operations table for {items.length} locations.
          </p>
        </div>
        <Button
          type="button"
          variant="default"
          size="sm"
          className="gap-1.5"
          onClick={openCreateDialog}
        >
          <Plus className="h-3.5 w-3.5" />
          Add Site
        </Button>
      </div>

      <div className="mb-3 rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-40)] bg-white px-3 py-2.5">
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="outline" size="sm" className="h-8" onClick={handleRefresh}>
            <span className="inline-flex items-center gap-1">
              <RefreshCw className="h-3 w-3" aria-hidden="true" />
              <span>Refresh</span>
            </span>
          </Button>
          <Button type="button" variant="outline" size="sm" className="h-8" onClick={openExportDialog}>
            <span className="inline-flex items-center gap-1">
              <FileText className="h-3 w-3" aria-hidden="true" />
              <span>Export</span>
            </span>
          </Button>
          {searchText && (
            <Button type="button" variant="outline" size="sm" className="h-8" onClick={() => handleSearch('')}>
              Clear Search
            </Button>
          )}
          {hasSecondaryFilters && (
            <Button type="button" variant="outline" size="sm" className="h-8" onClick={handleResetFilters}>
              Reset Filters
            </Button>
          )}
          <div className="ml-auto text-xs text-[var(--neutral-gray-100)]">
            {filteredItems.length} row{filteredItems.length === 1 ? '' : 's'}
          </div>
        </div>
      </div>

      <UnifiedTable<HousingDashboardItem>
        columns={columns}
        data={pagedItems}
        rowKey={(item) => String(item.id)}
        getRowStyle={(item) => getRowStyle(item)}
        className="h-[66vh]"
        density="compact"
        searchEnabled
        clientSearch={false}
        searchValue={searchDraft}
        onSearchValueChange={setSearchDraft}
        onSearchSubmit={handleSearch}
        searchPlaceholder="Search housing dashboard"
        loading={loading}
        emptyTitle="No housing rows found"
        emptyDescription="Try changing search or filters."
        emptyAction={
          hasActiveFilters ? (
            <div className="flex items-center justify-center gap-2">
              {searchText && (
                <Button type="button" variant="outline" size="sm" onClick={() => handleSearch('')}>
                  Clear search
                </Button>
              )}
              {hasSecondaryFilters && (
                <Button type="button" variant="outline" size="sm" onClick={handleResetFilters}>
                  Reset filters
                </Button>
              )}
            </div>
          ) : null
        }
        sortKey={String(sortField)}
        sortDirection={sortDirection}
        onSortChange={(key, direction) => {
          setSortField(key as keyof HousingDashboardItem);
          setSortDirection(direction);
        }}
        clientSort={false}
        toolbarLeft={
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-semibold uppercase tracking-wide text-[var(--neutral-gray-100)]">
                Search In
              </span>
              <Select
                value={searchField}
                onValueChange={(value) => {
                  setSearchField(value);
                  setPage(1);
                }}
              >
                <SelectTrigger className="h-[30px] w-[180px] text-xs">
                  <SelectValue aria-label="Search field" />
                </SelectTrigger>
                <SelectContent>
                  {searchOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-1.5">
              <span className="text-xs font-semibold uppercase tracking-wide text-[var(--neutral-gray-100)]">
                Program
              </span>
              <Select
                value={programFilter}
                onValueChange={(value) => {
                  setProgramFilter(value);
                  setPage(1);
                }}
              >
                <SelectTrigger className="h-[30px] w-[164px] text-xs">
                  <SelectValue aria-label="Program filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Programs</SelectItem>
                  {programOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-1.5">
              <span className="text-xs font-semibold uppercase tracking-wide text-[var(--neutral-gray-100)]">
                Active
              </span>
              <Select
                value={activeFilter}
                onValueChange={(value) => {
                  setActiveFilter(value);
                  setPage(1);
                }}
              >
                <SelectTrigger className="h-[30px] w-[100px] text-xs">
                  <SelectValue aria-label="Active filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        }
        toolbarRight={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0"
                disabled={displayedPage <= 1}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="min-w-[80px] text-center text-xs text-[var(--neutral-gray-100)]">
                {displayedPage} / {totalPages}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0"
                disabled={displayedPage >= totalPages}
                onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        }
        showRowActions
        stickyRowActions
        rowActionsHeader="Actions"
        renderRowActions={(item) => (
          <div className="flex items-center justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => openEditDialog(item)} className="gap-1.5">
              <Pencil className="h-3.5 w-3.5" />
              Edit
            </Button>
            <Button type="button" variant="outline" size="sm" disabled={togglingItemId !== null} onClick={() => handleToggleActive(item)}>
              {item.active ? 'Disable' : 'Enable'}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isDeleting}
              onClick={() => handleDelete(item)}
              className="gap-1.5 text-[var(--semantic-error)] hover:bg-[var(--semantic-error)]/5 hover:text-[var(--semantic-error)]"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete
            </Button>
          </div>
        )}
      />

      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent contentWidth="520px" contentMaxWidth="520px">
          <DialogHeader>
            <DialogTitle>Housing Report</DialogTitle>
            <DialogDescription>
              Export the legacy housing report using its own Program and Active filters.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Program</Label>
              <Select value={exportProgram} onValueChange={setExportProgram}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {exportProgramOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Active</Label>
              <div className="flex flex-wrap items-center gap-4 pt-1">
                <label className="flex items-center gap-2 text-sm text-[var(--neutral-gray-120)]">
                  <input
                    type="radio"
                    name="housing-export-active"
                    value="all"
                    checked={exportActive === 'all'}
                    onChange={() => setExportActive('all')}
                  />
                  All
                </label>
                <label className="flex items-center gap-2 text-sm text-[var(--neutral-gray-120)]">
                  <input
                    type="radio"
                    name="housing-export-active"
                    value="1"
                    checked={exportActive === '1'}
                    onChange={() => setExportActive('1')}
                  />
                  Active
                </label>
                <label className="flex items-center gap-2 text-sm text-[var(--neutral-gray-120)]">
                  <input
                    type="radio"
                    name="housing-export-active"
                    value="0"
                    checked={exportActive === '0'}
                    onChange={() => setExportActive('0')}
                  />
                  Disabled
                </label>
              </div>
            </div>
          </div>

          {exportError && <p role="alert" className="text-sm text-[var(--semantic-error)]">{exportError}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" size="sm" onClick={downloadHousingReport}>
              Download Report
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => setExportDialogOpen(false)}>
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogOpen} onOpenChange={(open: boolean) => { if (!isSaving) setDialogOpen(open); }}>
        <DialogContent contentWidth="1180px" contentHeight="85vh" contentMaxWidth="96vw">
          <DialogHeader>
            <DialogTitle>{formState.id == null ? 'Add Housing Site' : 'Edit Housing Site'}</DialogTitle>
            <DialogDescription>
              Full-row edit form over `housing_locations`. This preserves the legacy field contract without the PHP candidate framework.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 overflow-y-auto pr-2">
            <div className="grid gap-4 lg:grid-cols-3">
              <div className="space-y-2">
                <Label>Program</Label>
                <Select
                  value={formState.social_program || NONE_SELECT_VALUE}
                  onValueChange={(value) => setField('social_program', value === NONE_SELECT_VALUE ? '' : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select program" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE_SELECT_VALUE}>None</SelectItem>
                    {programOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end gap-3">
                <Switch checked={formState.active} onCheckedChange={(value) => setField('active', value)} />
                <Label>Active</Label>
              </div>
            </div>

            {formSections.map((section, sectionIndex) => (
              <div key={sectionIndex} className="grid gap-4 lg:grid-cols-3">
                {section.map((field) => {
                  if (field.key === 'social_program' || field.key === 'active') {
                    return null;
                  }

                  if (field.key === 'service_planning_area') {
                    return (
                      <div key={field.key} className="space-y-2">
                        <Label>{field.label}</Label>
                        <Select
                          value={formState.service_planning_area || NONE_SELECT_VALUE}
                          onValueChange={(value) => setField('service_planning_area', value === NONE_SELECT_VALUE ? '' : value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select SPA" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={NONE_SELECT_VALUE}>None</SelectItem>
                            {['1', '2', '3', '4', '5', '6', '7', '8'].map((value) => (
                              <SelectItem key={value} value={value}>{value}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  }

                  if (field.key === 'supervisorial_district') {
                    return (
                      <div key={field.key} className="space-y-2">
                        <Label>{field.label}</Label>
                        <Select
                          value={formState.supervisorial_district || NONE_SELECT_VALUE}
                          onValueChange={(value) => setField('supervisorial_district', value === NONE_SELECT_VALUE ? '' : value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select district" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={NONE_SELECT_VALUE}>None</SelectItem>
                            {['1', '2', '3', '4', '5'].map((value) => (
                              <SelectItem key={value} value={value}>{value}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  }

                  if (field.type === 'textarea') {
                    return (
                      <div key={field.key} className="space-y-2 lg:col-span-3">
                        <Label>{field.label}</Label>
                        <Textarea
                          rows={4}
                          value={`${formState[field.key] || ''}`}
                          onChange={(event) => setField(field.key, event.target.value)}
                        />
                      </div>
                    );
                  }

                  return (
                    <div key={field.key} className="space-y-2">
                      <Label>{field.label}</Label>
                      <Input
                        type={field.type === 'date' ? 'date' : 'text'}
                        value={`${formState[field.key] || ''}`}
                        onChange={(event) => setField(field.key, event.target.value)}
                      />
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-2">
            <Button disabled={isSaving} onClick={handleSave}>
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
            <Button variant="outline" disabled={isSaving} onClick={closeDialog}>
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
